import { useCallback, useEffect, useRef, useState } from 'react';
import {
  forgetComposerModule,
  readRecentModules,
  rememberComposerModule,
  resolveDefaultModule,
  type RecentModule,
} from '@entities/Module';
import type { ModuleSummary } from '@shared/types';

/** Откуда модуль в чипе — для события `home_cards_created` (ТЗ § 17). */
export type ModuleOrigin = 'default' | 'changed' | 'new';
/** loading — список ещё грузится; none — модулей нет; error — список не загрузился. */
export type ModulesStatus = 'loading' | 'ready' | 'none' | 'error';

export interface ComposerModule {
  id: string;
  title: string;
}

/** Сколько ждать список модулей, если «Создать» нажали до его прихода (ТЗ § 9). */
const MODULES_WAIT_MS = 15_000;

/**
 * Модуль строки создания (ТЗ главной § 9). По умолчанию — последний, куда добавляли карточки
 * (`lx.composer.modules.<userId>`), иначе первый в списке. Пока список грузится, чип показывает
 * название из истории — без мигания. Выбор в чипе действует, пока ученик на главной, и модуль по
 * умолчанию не меняет: его меняет только успешное создание (`remember`).
 */
export function useComposerModule({
  userId,
  modules,
  isError,
}: {
  userId: string | undefined;
  /** Свежий список модулей главной; undefined — ещё грузится. */
  modules: ModuleSummary[] | undefined;
  isError: boolean;
}) {
  const [recent, setRecent] = useState<RecentModule[]>(() => (userId ? readRecentModules(userId) : []));
  const [picked, setPicked] = useState<(ComposerModule & { origin: ModuleOrigin }) | null>(null);
  useEffect(() => {
    setRecent(userId ? readRecentModules(userId) : []);
    setPicked(null);
  }, [userId]);

  const fresh = picked && modules ? modules.find((m) => m.id === picked.id) : undefined;
  const fallback = modules ? resolveDefaultModule(recent, modules) : null;
  let module: ComposerModule | null = null;
  if (picked) module = { id: picked.id, title: fresh?.title ?? picked.title };
  else if (fallback) module = { id: fallback.id, title: fallback.title };
  else if (!modules && recent[0]) module = { id: recent[0].id, title: recent[0].title };

  const status: ModulesStatus = modules
    ? modules.length === 0 && !picked
      ? 'none'
      : 'ready'
    : isError
      ? 'error'
      : 'loading';

  // Модули, созданные в модалке в этот заход: список может ещё не успеть перечитаться.
  const created = useRef(new Set<string>());
  const modulesRef = useRef(modules);
  modulesRef.current = modules;
  const errorRef = useRef(isError);
  errorRef.current = isError;
  const waiters = useRef<(() => void)[]>([]);
  useEffect(() => {
    if (!modules && !isError) return;
    const list = waiters.current;
    waiters.current = [];
    list.forEach((wake) => wake());
  }, [modules, isError]);

  const select = useCallback((next: ComposerModule, origin: ModuleOrigin) => {
    if (origin === 'new') created.current.add(next.id);
    setPicked({ id: next.id, title: next.title, origin });
  }, []);

  /** Успешное создание: модуль первым в недавних — в следующий раз он по умолчанию. */
  const remember = useCallback(
    (next: ComposerModule) => {
      if (userId) setRecent(rememberComposerModule(userId, next));
    },
    [userId],
  );

  /** Модуль удалён в другой вкладке или на другом устройстве — из недавних и из чипа. */
  const forget = useCallback(
    (id: string) => {
      if (userId) setRecent(forgetComposerModule(userId, id));
      setPicked((current) => (current?.id === id ? null : current));
    },
    [userId],
  );

  /**
   * Перед созданием — сверка со свежим списком. Список ещё грузится — ждём его до 15 с.
   * `gone` — модуля нет (удалён), `failed` — список так и не загрузился.
   */
  const verify = useCallback(async (id: string): Promise<'ok' | 'gone' | 'failed'> => {
    if (created.current.has(id)) return 'ok';
    if (!modulesRef.current && !errorRef.current) {
      await new Promise<void>((resolve) => {
        const timer = window.setTimeout(resolve, MODULES_WAIT_MS);
        waiters.current.push(() => {
          window.clearTimeout(timer);
          resolve();
        });
      });
    }
    const list = modulesRef.current;
    if (!list) return 'failed';
    return list.some((m) => m.id === id) ? 'ok' : 'gone';
  }, []);

  return {
    module,
    status,
    recent,
    origin: picked?.origin ?? ('default' as ModuleOrigin),
    select,
    remember,
    forget,
    verify,
  };
}

export type ComposerModuleState = ReturnType<typeof useComposerModule>;
